import React, { useState } from 'react';
import { ArrowLeft, Flower2, Copy, Check } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';

const Roses100 = () => {
  const [copied, setCopied] = useState(false);
  const navigate = useNavigate();
  const currentYear = new Date().getFullYear();

  const roses = '🌹'.repeat(100);

  const handleCopy = () => {
    navigator.clipboard.writeText(roses);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const title = `100 Red Roses 🌹 Copy and Paste [${currentYear}] Free Rose Collection`;
  const description = `【INSTANT COPY】 Get 100 beautiful red roses! 🌹 Perfect for expressing love and romance 🌹 One-click copy & paste ⚡ Works everywhere! Updated for ${currentYear} ✨ Free rose emojis for Instagram, WhatsApp & more! #1 Rose Collection`;

  // Rich snippets for better SEO
  const structuredData = {
    "@context": "https://schema.org",
    "@type": "WebApplication",
    "name": `100 Roses Generator [${currentYear}]`,
    "description": `Generate and copy 100 beautiful red roses 🌹. Updated for ${currentYear} with new rose emojis and designs. Perfect for expressing love and romance!`,
    "applicationCategory": "UtilityApplication",
    "operatingSystem": "Any",
    "offers": {
      "@type": "Offer",
      "price": "0",
      "priceCurrency": "USD"
    },
    "aggregateRating": {
      "@type": "AggregateRating",
      "ratingValue": "4.9",
      "ratingCount": "1832",
      "bestRating": "5",
      "worstRating": "1"
    }
  };

  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": [
      {
        "@type": "Question",
        "name": "How do I copy 100 roses?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Simply click the 'Copy 100 Roses' button and all roses will be copied to your clipboard. You can then paste them anywhere using Ctrl+V (Windows) or Cmd+V (Mac)."
        }
      },
      {
        "@type": "Question",
        "name": "Do these roses work everywhere?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Yes! Our rose emojis work on all major platforms including Instagram, Facebook, WhatsApp, Twitter, TikTok, and more. They're compatible with both mobile and desktop devices."
        }
      },
      {
        "@type": "Question",
        "name": "What does the rose emoji symbolize?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "The rose emoji 🌹 symbolizes love, romance, beauty, and passion. It's commonly used for expressing romantic feelings, appreciation, and celebrating special occasions."
        }
      }
    ]
  };

  return (
    <>
      <Helmet>
        <title>{title}</title>
        <meta name="description" content={description} />
        <link rel="canonical" href="https://emojicollection.net/roses-100" />
        
        <meta property="og:title" content={title} />
        <meta property="og:description" content={description} />
        <meta property="og:url" content="https://emojicollection.net/roses-100" />
        <meta property="og:type" content="website" />
        <meta property="og:image" content="https://images.unsplash.com/photo-1548094990-c16ca90f1f0d?auto=format&fit=crop&w=1200&h=630&q=80" />
        
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={title} />
        <meta name="twitter:description" content={description} />
        <meta name="twitter:image" content="https://images.unsplash.com/photo-1548094990-c16ca90f1f0d?auto=format&fit=crop&w=1200&h=630&q=80" />
        
        <meta name="keywords" content="100 roses, rose emoji, red roses, rose emoji copy paste, romantic emojis, love emoji, instagram roses, whatsapp roses, facebook roses, rose symbols, flower emojis" />
        <meta name="robots" content="index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1" />
        <meta name="author" content="Emoji Collection" />
        <meta name="revisit-after" content="1 days" />
        
        <script type="application/ld+json">
          {JSON.stringify(structuredData)}
        </script>
        <script type="application/ld+json">
          {JSON.stringify(faqSchema)}
        </script>
      </Helmet>

      <div className="min-h-screen bg-gray-50 py-12">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <button
            onClick={() => navigate('/')}
            className="inline-flex items-center text-gray-600 hover:text-gray-900 mb-6 transition-colors"
          >
            <ArrowLeft className="mr-2" size={20} />
            Back to Home
          </button>

          <article className="bg-white rounded-2xl shadow-lg p-8">
            <header className="text-center mb-8">
              <img 
                src="https://images.unsplash.com/photo-1548094990-c16ca90f1f0d?auto=format&fit=crop&w=1200&q=80"
                alt="Beautiful red roses symbolizing love and romance"
                className="w-full h-48 object-cover rounded-xl mb-8"
              />
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-red-100 text-red-500 mb-4">
                <Flower2 className="w-8 h-8" />
              </div>
              <h1 className="text-3xl font-bold text-gray-900 mb-4">
                100 Red Roses 🌹 Copy and Paste [{currentYear}]
              </h1>
              <p className="text-lg text-gray-600 mb-8">
                Get instant access to 100 beautiful red rose emojis! Perfect for expressing love and romance on Instagram, WhatsApp, Facebook, and more. Updated for {currentYear} with the latest rose emojis. One-click copy and paste feature!
              </p>
            </header>

            <section className="max-w-2xl mx-auto mb-12">
              <div className="bg-gray-50 rounded-lg p-6">
                <h2 className="text-xl font-semibold text-gray-900 mb-4">
                  🌹 Copy 100 Roses Instantly
                </h2>
                <div className="mb-4 overflow-auto max-h-96 text-left break-words text-2xl">
                  {roses}
                </div>
                <button
                  onClick={handleCopy}
                  className={`inline-flex items-center px-6 py-3 rounded-lg text-lg font-medium transition-all transform hover:scale-105 ${
                    copied
                      ? 'bg-green-500 text-white'
                      : 'bg-gradient-to-r from-red-500 to-pink-500 text-white hover:from-red-600 hover:to-pink-600'
                  }`}
                >
                  {copied ? (
                    <>
                      <Check className="mr-2" size={20} />
                      Copied 100 Roses!
                    </>
                  ) : (
                    <>
                      <Copy className="mr-2" size={20} />
                      Copy 100 Roses
                    </>
                  )}
                </button>
              </div>
            </section>

            <section className="border-t border-gray-200 pt-8">
              <h2 className="text-2xl font-semibold mb-4">✨ Features & Benefits</h2>
              <div className="text-gray-600 space-y-4">
                <p>🌹 100 beautiful red rose emojis</p>
                <p>💝 Perfect for expressing love and romance</p>
                <p>📋 Instant one-click copy functionality</p>
                <p>💕 Works on all platforms and devices</p>
                <p>🆕 Updated for {currentYear} with latest emojis</p>
                <p>🔄 Ready to spread love and beauty</p>
              </div>
            </section>

            <section className="border-t border-gray-200 mt-8 pt-8">
              <h2 className="text-2xl font-semibold mb-4">📱 Perfect For</h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div>
                  <h3 className="text-lg font-medium mb-2">Romance</h3>
                  <ul className="list-disc list-inside space-y-2 text-gray-600">
                    <li>Love messages</li>
                    <li>Valentine's Day</li>
                    <li>Anniversaries</li>
                    <li>Date nights</li>
                  </ul>
                </div>
                <div>
                  <h3 className="text-lg font-medium mb-2">Social Media</h3>
                  <ul className="list-disc list-inside space-y-2 text-gray-600">
                    <li>Instagram posts</li>
                    <li>Facebook updates</li>
                    <li>Twitter messages</li>
                    <li>TikTok captions</li>
                  </ul>
                </div>
                <div>
                  <h3 className="text-lg font-medium mb-2">Special Events</h3>
                  <ul className="list-disc list-inside space-y-2 text-gray-600">
                    <li>Weddings</li>
                    <li>Engagements</li>
                    <li>Celebrations</li>
                    <li>Milestones</li>
                  </ul>
                </div>
              </div>
            </section>

            <section className="border-t border-gray-200 mt-8 pt-8">
              <h2 className="text-2xl font-semibold mb-4">❓ Frequently Asked Questions</h2>
              <div className="space-y-4">
                <details className="group bg-gray-50 rounded-lg">
                  <summary className="cursor-pointer p-4 font-medium">Why use rose emojis?</summary>
                  <div className="p-4 pt-0 text-gray-600">
                    Rose emojis are universally recognized symbols of love, beauty, and appreciation. They add a romantic and elegant touch to your messages and social media posts.
                  </div>
                </details>
                <details className="group bg-gray-50 rounded-lg">
                  <summary className="cursor-pointer p-4 font-medium">Do these roses work everywhere?</summary>
                  <div className="p-4 pt-0 text-gray-600">
                    Yes! These rose emojis are universal and work on all major platforms including Instagram, Facebook, WhatsApp, and more. They're compatible with both mobile and desktop devices.
                  </div>
                </details>
                <details className="group bg-gray-50 rounded-lg">
                  <summary className="cursor-pointer p-4 font-medium">Are these roses free to use?</summary>
                  <div className="p-4 pt-0 text-gray-600">
                    Absolutely! Our rose emojis are completely free to use for both personal and commercial purposes. No attribution required.
                  </div>
                </details>
              </div>
            </section>
          </article>
        </div>
      </div>
    </>
  );
};

export default Roses100;